import { useQuery, useQueries } from "@tanstack/react-query"
import { getProvisionsByName, getRiderProvisions, getVendorProvisionsByName, parseProvisionVars } from "../../../utils/planUtils"
import { fetchValueFromValueList } from "../../../utils/apiUtils"
import { useEffect, useState } from "react"
import { downloadFile, formatCustomDate } from "../../../utils/downloadUtils"

const step = 19

const provisionQueries = (getValues) => {
  const accumulator = getValues('accumulatorFromMapping')
  const costSharing = getValues('costShareBenefitDetails')
  const extraRiders = getValues('extraRiders')
  const extraDefaultComponents = getValues('extraDefaultComponents')
  const exclusions = getValues('exclusions')
  const limits = getValues('limits')
  const nonParticipatingPricingTrueOutOfNetwork = getValues('nonParticipatingPricingTrueOutOfNetwork')
  const vendors = getValues("vendors")
  const cob = getValues('cob.default')
  const networkDefinition = getValues('networkDefinitionRider')
  const preCert = getValues('preCertRules')
  const repricer = getValues('repricer')
  const repricerDefault = getValues('repricerRuleRiderDefault')
  const pcp = getValues('rules.pcp.penaltyOption')
  const referral = getValues('rules.referral')
  const templateKey = getValues('template.additional.benefitPlanTemplateKey')

  // collect all riders that don't need rider lookups
  let costSharingRiders = Object.values(costSharing)
    .map(i => Object.values(i))
    .flat()
    .filter(i => i.type)
    .map(i => i.type.additional)

  costSharingRiders.concat(extraRiders)
  costSharingRiders.concat(extraDefaultComponents)
  if (exclusions.default?.additional) costSharingRiders.push(exclusions.default?.additional)
  if (exclusions.clientSpecific?.additional) costSharingRiders.push(exclusions.clientSpecific?.additional)
  if (limits.default?.additional) costSharingRiders.push(limits.default?.additional)
  if (limits.overwriteDefault?.additional) costSharingRiders.push(limits.overwriteDefault?.additional)
  if (nonParticipatingPricingTrueOutOfNetwork?.additional) costSharingRiders.push(nonParticipatingPricingTrueOutOfNetwork?.additional)
  if (cob?.additional) costSharingRiders.push(cob.additional)
  if (networkDefinition?.additional) costSharingRiders.push(networkDefinition.additional)
  if (preCert.serviceRequireingPreCert?.additional) costSharingRiders.push(preCert.serviceRequireingPreCert.additional)
  if (repricer?.additional) costSharingRiders.push(repricer.additional)
  if (repricerDefault?.additional) costSharingRiders.push(repricerDefault.additional)
  if (pcp?.additional) costSharingRiders.push(pcp.additional)
  if (referral.default?.additional) costSharingRiders.push(referral.default.additional)
  if (referral.penaltyOption?.additional) costSharingRiders.push(referral.penaltyOption.additional)

  let queries = costSharingRiders.map(r => ({
    queryKey: ['provisions', r.bnftPlanCompHistFactKey],
    queryFn: async () => await getRiderProvisions(r.bnftPlanCompHistFactKey),
    select: (provisions) => provisions.map(p => ({ ...p, category: r.slotName })),
    staleTime: Infinity,
  }))

  queries.push({
    queryKey: ['provisions', accumulator?.label],
    queryFn: async () => await getProvisionsByName(templateKey, "OOP Max - Deductible", accumulator.label),
    select: (rider) => rider.provisions.map(p => ({ ...p, category: "OOP Max - Deductible" })),
    enabled: !!accumulator,
    staleTime: Infinity,
  })

  queries = queries.concat(
    Object.entries(vendors)
          .filter(([_, v]) => v)
          .map(([k, _]) => ({
            queryKey: ['provisions', k],
            queryFn: async () => await getVendorProvisionsByName(templateKey, k),
            select: (provisions) => provisions.map(p => ({ ...p, category: "Vendor" })),
            staleTime: Infinity,
          }))
  )
  return useQueries({
    queries: queries,
    combine: (results) => ({
      data: results.map(r => r.data).filter(Boolean).flat(),
      isPending: results.some(r => r.isPending),
    })
  })
}

const valueListQuery = (getValues) => {
  const planKey = getValues('benefitPlanKey')
  const planHistKey = getValues('benefitPlanHistoryFactKey')

  return useQuery({
    queryKey: ['value-list', 'provisions', planKey, planHistKey],
    queryFn: async () => await fetchValueFromValueList({
      benefitPlanKey: planKey,
      benefitPlanHistoryFactKey: planHistKey,
    }, 500),
    select: (data) => data.rows,
    enabled: !!(planKey && planHistKey),
    staleTime: Infinity,
  })
}

const VariableRow = ({ variable, register }) => {
  let vName = `variableValues.${variable.name}`
  let color = 'white'
  if (variable.required && variable.exists) color = '#90EE90'
  else if (variable.required && !variable.exists) color = '#FFFACD'
  else if (variable.exists && !variable.required) color = '#D3D3D3'
  return (
    <tr key={variable.name} style={{ backgroundColor: color }} >
      <td>{variable.category}</td>
      <td>{variable.name}</td>
      <td>{variable.required ? "true" : "false"}</td>
      <td>{variable.exists ? "true" : "false"}</td>
      <td>{variable.value}</td>
      <td>
        <input {...register(vName, {setValueAs: variable => Number(variable)})}
               type="number"
               id={vName}
               name={vName} />
      </td>
    </tr>
  )
}

const ValueListTable = ({ valueList, register }) => (
  <table>
    <tbody>
      <tr>
        <th>Category</th>
        <th>Name</th>
        <th>Required?</th>
        <th>In VL?</th>
        <th>Value</th>
        <th>Desired</th>
      </tr>
      {valueList.map((v, i) => <VariableRow key={i} variable={v} register={register} />)}
    </tbody>
  </table>
)

const StepNineteen = ({ activeStep, register, setValue, getValues }) => {
  if (activeStep !== step) return

  const [isDownloading, setIsDownloading] = useState(false)
  const [valueList, setValueList] = useState([])
  const variableValues = getValues('variableValues')

  const {
    data: provisionsData,
    isPending: provisionsPending,
  } = provisionQueries(getValues)

  const {
    data: vlData,
    isPending: vlPending,
  } = valueListQuery(getValues)

  useEffect(
    () => {
      if (provisionsPending || vlPending) {
        return
      }

      let tmpValueList = (provisionsData || []).reduce((a, p) => {
        let pVars = parseProvisionVars(p.benefitProvisionText)
        pVars && pVars.forEach(name => {
          a[name] = {
            name,
            category: p.category,
            required: true,
          }
        })
        return a
      }, {})

      tmpValueList = (vlData || []).reduce((a, vl) => {
        const rec = a[vl.valueName]
        const count = vl?.countValue
        const currency = vl?.currencyValue
        const percent = vl?.percentValue
        const value = vl?.countValue || vl?.currencyValue || vl?.percentValue
        a[vl.valueName] = {
          name: vl.valueName,
          exists: true,
          count,
          currency,
          percent,
          value,
          ...rec,
        }
        if (!variableValues) setValue(`variableValues.${vl.valueName}`, value)
        return a
      }, tmpValueList)

      const sortedVars = Object.values(tmpValueList).sort((a, b) => {
        let aCategory = a?.category || 'ZZZ' // sort at the end
        let bCategory = b?.category || 'ZZZ'
        if (aCategory !== bCategory) return aCategory.localeCompare(bCategory)
        return a.name.localeCompare(b.name)
      })

      setValueList(sortedVars)
    }, [provisionsData, provisionsPending, vlData, vlPending])

  const download = async () => {
    // exportToCsv is opinionated and doesn't like numerical values
    setIsDownloading(true)
    try {
      const planKey = getValues('benefitPlanKey')
      const fileName = `${planKey}-variable-list-${formatCustomDate(new Date())}.csv`
      const fileType = 'text/csv'

      const headers = ['category', 'name', 'required', 'inValueList', 'value', 'desired', 'valueHrl', 'desiredHrl']
      let data = [headers.join(",")]
      valueList.forEach(v => {
        const vV = (v.count) ? v.count : (v.currency) ? `$${v.currency}` : (v.percent) ? `${v.percent} %` : v.value
        const hrlV = `variable "${v.name}" has value ${vV}`
        const desired = (variableValues && v.name in variableValues) ?
              variableValues[v.name] : ''
        const hrlD = `variable "${v.name}" has value ${desired}`
        let datums = [
          v.category || '',
          v.name,
          !!v.required,
          !!v.exists,
          v.value || '',
          desired,
          (v.value) ? hrlV : '',
          (desired != '') ? hrlD : '',
        ]
        data.push(datums.join(","))
      })
      data = data.join("\n")
      downloadFile({ data, fileName, fileType })
    } finally {
      setIsDownloading(false)
    }
  }

  const isPending = provisionsPending || vlPending

  return (
    <div>
      {isPending ?
        <div className="loader" />
        :
        <div>
          {isDownloading ?
            <div className="loader" /> :
            <p className="clickable-link"
               style={{
                 color: 'var(--green)',
                 padding: '1rem',
               }}
               onClick={download} >
              Download
            </p>}
          <ValueListTable valueList={valueList} register={register} />
        </div>}
    </div>
  )
}

export default StepNineteen
